import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
// Ensure you have `putRequest` imported and defined in utils/http.js if you want to use the updateById route
import { getRequest, postRequest, deleteRequest } from 'utils/http' // Make sure putRequest is available

// =========================================================
//             SETTINGS QUERIES
// =========================================================

/**
 * Fetches the single site settings record.
 * Backend: GET /api/settings (calls SettingController@index)
 * Expects: An array containing one setting object, e.g., [{ id: 1, site_name: '...' }]
 */
export const getSettings = async () => {
  return getRequest('settings')
}

/**
 * Hook to fetch the single site settings record.
 * Selects the first item from the array returned by the backend.
 */
export const useGetSettings = () => {
  return useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    // The API response has { status, message, data: [...] }, so extract data[0]
    select: data =>
      data?.data && Array.isArray(data.data) && data.data.length > 0
        ? data.data[0]
        : null,
    // staleTime: Infinity, // Settings don't change often, consider this
    // refetchOnWindowFocus: false, // Prevent unnecessary refetches on focus
  })
}

/**
 * Creates or Updates the single site settings record using your custom POST /api/setting route.
 * Backend: POST /api/setting (calls SettingController@storeOrUpdate)
 */
export const createOrUpdateSetting = async params => {
  // Use 'setting' (singular) to hit your custom route
  return postRequest('setting', params)
}

/**
 * Hook to create or update the site settings record.
 * On success, it invalidates the 'settings' query to trigger a refetch.
 */
export const useCreateOrUpdateSetting = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: params => createOrUpdateSetting(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: error => {
      console.error('Failed to create or update settings:', error)
      // Implement UI feedback here (e.g., toast.error)
    },
  })
}

// =========================================================
// Deletion (if still desired, but generally not for global settings)
// =========================================================
/**
 * Deletes the single site settings record.
 * Backend: DELETE /api/settings/{id} (calls SettingController@destroy)
 * @param {string} id - The ID of the settings record to delete (expected to be '1').
 */
export const deleteSettings = async id => {
  return deleteRequest(`settings/${id}`)
}

/**
 * Hook to delete the site settings record.
 * On success, it invalidates the 'settings' query to reflect the change.
 */
export const useDeleteSettings = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: id => deleteSettings(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      // You might also redirect or show a message that settings are gone
    },
    onError: error => {
      console.error('Failed to delete settings:', error)
    },
  })
}


